/**
 * Shared filter model for the Service Desk dashboard + its drill-in
 * tickets page. Clicking a metric on the dashboard navigates to
 * /service-desk/tickets?f=<serialized filter>; the tickets page parses
 * the same param back. Keeping the encode/decode/match logic here means
 * the two surfaces can't drift.
 */
import type { ServiceDeskTicket } from "@/hooks/useFreshservice";

export type Filter =
  | { kind: "all" }
  | { kind: "priority"; value: number }
  | { kind: "group"; value: string }
  | { kind: "age"; value: string } // AGE_BUCKETS key
  | { kind: "type"; value: "incident" | "request" }
  | { kind: "major" }
  | { kind: "unassigned" }
  | { kind: "aged30" }
  | null;

export const PRIORITY_ORDER = [4, 3, 2, 1]; // Highest → Low

export const PRIORITY_COLOR: Record<number, { bar: string; text: string }> = {
  4: { bar: "bg-red-500", text: "text-red-700 dark:text-red-300" },
  3: { bar: "bg-amber-500", text: "text-amber-700 dark:text-amber-300" },
  2: { bar: "bg-sky-500", text: "text-sky-700 dark:text-sky-300" },
  1: { bar: "bg-slate-400", text: "text-ink-3" },
};

export const AGE_BUCKETS = [
  { key: "0_7", label: "0–7 days", min: 0, max: 7, bar: "bg-emerald-500" },
  { key: "7_30", label: "7–30 days", min: 7, max: 30, bar: "bg-sky-500" },
  { key: "30_90", label: "30–90 days", min: 30, max: 90, bar: "bg-amber-500" },
  { key: "90_plus", label: "90+ days", min: 90, max: Infinity, bar: "bg-red-500" },
];

export function daysAgo(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z").getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

export function isIncident(t: ServiceDeskTicket): boolean {
  return /incident/i.test(t.type ?? "");
}

// Major incident: an explicit "Major Incident" type if FS uses one,
// otherwise a Highest-priority incident. Surfaced as a note on the
// dashboard so it can be corrected if FS flags them elsewhere.
export function isMajorIncident(t: ServiceDeskTicket): boolean {
  if (/major/i.test(t.type ?? "")) return true;
  return isIncident(t) && t.priority >= 4;
}

export function parseFilter(raw: string | null): Filter {
  if (!raw) return null;
  if (raw === "all") return { kind: "all" };
  if (raw === "major") return { kind: "major" };
  if (raw === "unassigned") return { kind: "unassigned" };
  if (raw === "aged30") return { kind: "aged30" };
  const [kind, value] = raw.split(":");
  if (kind === "priority" && value) return { kind: "priority", value: Number(value) };
  if (kind === "group" && value) return { kind: "group", value };
  if (kind === "age" && value) return { kind: "age", value };
  if (kind === "type" && (value === "incident" || value === "request"))
    return { kind: "type", value };
  return null;
}

export function serializeFilter(f: Filter): string | null {
  if (!f) return null;
  switch (f.kind) {
    case "all": return "all";
    case "major": return "major";
    case "unassigned": return "unassigned";
    case "aged30": return "aged30";
    case "priority": return `priority:${f.value}`;
    case "group": return `group:${f.value}`;
    case "age": return `age:${f.value}`;
    case "type": return `type:${f.value}`;
  }
}

export function filterMatches(t: ServiceDeskTicket, f: Filter): boolean {
  if (!f) return true;
  switch (f.kind) {
    case "all": return true;
    case "priority": return t.priority === f.value;
    case "group": return (t.group_name?.trim() || "No group") === f.value;
    case "type": return f.value === "incident" ? isIncident(t) : !isIncident(t);
    case "major": return isMajorIncident(t);
    case "unassigned": return t.agent_id == null;
    case "aged30": {
      const age = daysAgo(t.created_at);
      return age != null && age >= 30;
    }
    case "age": {
      const bucket = AGE_BUCKETS.find((b) => b.key === f.value);
      if (!bucket) return true;
      const age = daysAgo(t.created_at);
      return age != null && age >= bucket.min && age < bucket.max;
    }
  }
}

export function filterLabel(f: Filter, priorityLabels: Record<number, string>): string {
  if (!f) return "Tickets";
  switch (f.kind) {
    case "all": return "All unresolved tickets";
    case "priority": return `${priorityLabels[f.value] ?? f.value} priority`;
    case "group": return f.value;
    case "type": return f.value === "incident" ? "Incidents (break/fix)" : "Service requests";
    case "major": return "Major incidents";
    case "unassigned": return "Unassigned";
    case "aged30": return "Aged 30+ days";
    case "age": return AGE_BUCKETS.find((b) => b.key === f.value)?.label ?? "Aged";
  }
}

/** Build the drill-in URL the dashboard links to. */
export function ticketsHref(f: Filter): string {
  const ser = serializeFilter(f);
  return ser ? `/service-desk/tickets?f=${encodeURIComponent(ser)}` : "/service-desk/tickets";
}
